import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { backtestAPI } from '../../../api/backtests';
import { tok, LABEL, MONO } from '../results/ui';

// Ticked runs' equity curves overlaid, each as % change from its own start, on real dates
// (DWLF-306; canvas board BacktestList). Full results are fetched only for the 2–3 runs compared.

const W = 1000;
const H = 180;
const SERIES = ['accent', 'warn', 'up'];
const LOG_FLOOR = 0.01; // 1% of starting equity

// A run's whole-account curve: the portfolio curve, or the only symbol's curve for a single-symbol
// run. A multi-symbol run without a portfolio curve has no single curve to compare.
export const curveOf = (payload) => {
  const results = payload?.data?.results || payload?.results || [];
  const r = results[0];
  let pts = [];
  if (r?.portfolioEquityCurve?.length) pts = r.portfolioEquityCurve;
  else if (results.length === 1) pts = r?.equityCurve || [];
  else return 'incomparable';
  const first = pts.find((p) => Number.isFinite(p?.equity) && p.equity > 0);
  if (!first) return [];
  return pts.filter((p) => Number.isFinite(p?.equity)).map((p) => ({ t: new Date(p.date).getTime(), v: ((p.equity / first.equity) - 1) * 100 }));
};

export default function CompareBar({ runs, onClear }) {
  const [curves, setCurves] = useState({});
  // Log scale: equal heights for equal percentage moves, so a long run's late swings don't dwarf its
  // early ones. Plots ln(growth).
  const [log, setLog] = useState(false);
  const ids = runs.map((r) => r.requestId).join(',');

  // Each run's curve is fetched once and kept; ticking or unticking others never abandons a fetch
  // (only unmounting does), so a series cannot stall on 'loading'.
  const requested = useRef(new Set());
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    runs.forEach((r) => {
      if (requested.current.has(r.requestId)) return;
      requested.current.add(r.requestId);
      setCurves((c) => ({ ...c, [r.requestId]: 'loading' }));
      backtestAPI.getBacktestResults(r.requestId)
        .then((res) => { if (mounted.current) setCurves((c) => ({ ...c, [r.requestId]: curveOf(res.data) })); })
        .catch(() => {
          requested.current.delete(r.requestId); // let a later tick retry
          if (mounted.current) setCurves((c) => ({ ...c, [r.requestId]: 'failed' }));
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs is keyed by ids
  }, [ids]);

  const series = runs.map((r, i) => ({ run: r, color: SERIES[i % SERIES.length], pts: curves[r.requestId] }));
  const drawn = series.filter((s) => Array.isArray(s.pts) && s.pts.length > 1);
  const geo = useMemo(() => {
    if (!drawn.length) return null;
    const all = drawn.flatMap((s) => s.pts);
    const t0 = Math.min(...all.map((p) => p.t)); const t1 = Math.max(...all.map((p) => p.t));
    // A point at or below zero equity has no log; it is clamped to the floor so one blown-up run
    // can't turn every curve's scale into NaN.
    const f = (v) => (log ? Math.log(Math.max(1 + v / 100, LOG_FLOOR)) : v);
    const lo = Math.min(0, ...all.map((p) => f(p.v))); const hi = Math.max(0, ...all.map((p) => f(p.v)));
    const x = (t) => ((t - t0) / Math.max(1, t1 - t0)) * W;
    const y = (v) => H - ((f(v) - lo) / Math.max(1e-9, hi - lo)) * H;
    return { x, y, zeroY: y(0), lo, hi, paths: drawn.map((s) => ({ color: s.color, d: s.pts.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)} ${y(p.v).toFixed(1)}`).join(' ') })) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curves, ids, log]);

  return (
    <Box role="region" aria-label="Compare backtests" sx={{ position: 'sticky', bottom: 0, mt: 2, border: `1px solid ${tok('accent')}`, backgroundColor: tok('elevated'), p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px minmax(0, 1fr)' }, gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography sx={{ ...LABEL, fontSize: '0.6875rem', color: tok('accent') }}>Compare ({runs.length})</Typography>
        {series.map((s) => (
          <Box key={s.run.requestId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="span" sx={{ width: 14, height: 3, flexShrink: 0, backgroundColor: tok(s.color) }} />
            <Typography noWrap sx={{ fontFamily: MONO, fontSize: '0.75rem', color: tok('text') }}>{s.run.strategyName}</Typography>
            {s.pts === 'loading' && <Typography component="span" sx={{ fontFamily: MONO, fontSize: '0.6875rem', color: tok('ink-50') }}>loading…</Typography>}
            {s.pts === 'failed' && <Typography component="span" sx={{ fontFamily: MONO, fontSize: '0.6875rem', color: tok('down') }}>failed</Typography>}
            {s.pts === 'incomparable' && <Typography component="span" sx={{ fontFamily: MONO, fontSize: '0.6875rem', color: tok('ink-55') }}>no whole-run curve (multi-symbol, not portfolio mode)</Typography>}
          </Box>
        ))}
        <Typography sx={{ fontFamily: MONO, fontSize: '0.6875rem', color: tok('ink-50') }}>
          Equity, % from each run's start, on real dates{log ? ', log scale' : ''}.
        </Typography>
        <ButtonBase aria-pressed={log} onClick={() => setLog((v) => !v)}
          sx={{ ...LABEL, alignSelf: 'flex-start', fontSize: '0.625rem', height: 28, px: 1.25, border: `1px solid ${tok(log ? 'accent' : 'ink-15')}`, color: tok(log ? 'accent' : 'ink-60') }}>
          Log scale
        </ButtonBase>
        <ButtonBase onClick={onClear} sx={{ ...LABEL, alignSelf: 'flex-start', fontSize: '0.625rem', height: 32, color: tok('ink-60') }}>Clear</ButtonBase>
      </Box>
      <Box>
        {geo ? (
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" role="img" aria-label="Overlaid equity curves">
            <line x1={0} x2={W} y1={geo.zeroY} y2={geo.zeroY} style={{ stroke: tok('ink-15') }} strokeDasharray="3 4" />
            {geo.paths.map((p, i) => <path key={i} d={p.d} fill="none" strokeWidth={1.6} vectorEffect="non-scaling-stroke" style={{ stroke: tok(p.color) }} />)}
          </svg>
        ) : (
          <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok('ink-50'), pt: 2 }}>Loading curves…</Typography>
        )}
      </Box>
    </Box>
  );
}
